// @flow
/* eslint-disable ta-code-conventions/has-matching-test,ta-code-conventions/has-matching-storydoc */

import * as React from 'react';
import { connect } from 'react-redux';
import { RouteConsumer } from '@ta/common.routing';
import WindowScroll from '@ta/events.window-scroll';
import type { ViewportCategory } from '@ta/common.state';
import { featureIsEnabled } from '@ta/common.features';
import sessionStorage from '@ta/common.sessionstorage';
import { getState } from '@ta/social.tooltip-state';

import LoginBar from './LoginBar';

/**
 *
 * For a logged out user on Scoped Home, show a login bar after scrolling past top of Feed + constant number of pixels. TRVX-11324
 */

type ExternalProps = {|
  feedImpressionId: ?string,
  topOfFeed: React.ElementRef<*>,
|};

type Props = {|
  isUserLoggedIn: boolean,
  puid: string,
  viewportCategory: ViewportCategory,
  shouldForceShowViaRedux: boolean,
  ...ExternalProps,
|};

type State = {|
  hasScrolledPastSecondSection: boolean,
  shouldForceShowViaSessionStorage: boolean,
|};

const NUM_PIXEL_OFFSET_FROM_TOP_OF_FEED = 300;
const THROTTLE_WAIT = 600;

/** Local storage key to use to force show login bar on scoped home only */
const FORCE_LOGIN_BAR = 'show_scoped_home_login_bar';

class ScopedHomeLoggedOutUsersLoginBar extends React.Component<Props, State> {
  state = { hasScrolledPastSecondSection: false, shouldForceShowViaSessionStorage: false };

  componentDidMount() {
    if (sessionStorage.has(FORCE_LOGIN_BAR)) {
      this.setState({ shouldForceShowViaSessionStorage: true });
      sessionStorage.remove(FORCE_LOGIN_BAR);
    }
  }

  onScroll = () => {
    if (
      !this.state.hasScrolledPastSecondSection &&
      this.props.topOfFeed.current.getBoundingClientRect().top + NUM_PIXEL_OFFSET_FROM_TOP_OF_FEED <
        0
    ) {
      this.setState({ hasScrolledPastSecondSection: true });
    }
  };

  getProductId = () =>
    this.props.shouldForceShowViaRedux || this.state.shouldForceShowViaSessionStorage ? 40510 : -1;

  isScopedHome = (route: { [string]: string }) =>
    'latitude' in route || 'longitude' in route || 'geo' in route;

  isGhost = () => {
    if (this.props.shouldForceShowViaRedux || this.state.shouldForceShowViaSessionStorage) {
      return false;
    }

    return !featureIsEnabled('social_scoped_home_login_bar');
  };

  shouldBeOnPage = (route: { [string]: string }) =>
    !this.props.isUserLoggedIn &&
    this.props.viewportCategory === 'DESKTOP' &&
    (this.isScopedHome(route) ||
      this.props.shouldForceShowViaRedux ||
      this.state.shouldForceShowViaSessionStorage);

  shouldBeVisible = (route: { [string]: string }) =>
    this.shouldBeOnPage(route) && this.state.hasScrolledPastSecondSection;

  render() {
    return (
      <RouteConsumer>
        {({ route }) =>
          this.shouldBeOnPage(route) && (
            <React.Fragment>
              <WindowScroll callback={this.onScroll} throttle={THROTTLE_WAIT} />
              <LoginBar
                shouldShow={this.shouldBeVisible(route)}
                trackingOnLoginClick={{
                  module: 'social_tooltip | login_bar_login',
                  action: 'click',
                  context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                  parentUid: this.props.puid,
                }}
                trackingOnJoinClick={{
                  module: 'social_tooltip | login_bar_join',
                  action: 'click',
                  context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                  parentUid: this.props.puid,
                }}
                trackingOnShow={
                  this.isGhost()
                    ? {
                        module: 'social_tooltip | login_bar_ghost',
                        action: 'impression',
                        context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                        parentUid: this.props.puid,
                      }
                    : {
                        module: 'social_tooltip | login_bar',
                        action: 'impression',
                        context: this.props.feedImpressionId ? this.props.feedImpressionId : '',
                        parentUid: this.props.puid,
                      }
                }
                isGhost={this.isGhost()}
                productId={this.getProductId()}
              />
            </React.Fragment>
          )
        }
      </RouteConsumer>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isUserLoggedIn: !!state.auth.loggedInUserId,
  puid: state.tracking.uid,
  viewportCategory: state.meta.device.viewportCategory,
  shouldForceShowViaRedux: getState(state).forceShowLoginBar,
}))(ScopedHomeLoggedOutUsersLoginBar);
